import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

type Toast = { id: number; text: string; tone: "ok" | "danger" | "muted"; href?: string };
const Ctx = createContext<(text: string, tone?: Toast["tone"], href?: string) => void>(() => {});

export function useToast() {
  return useContext(Ctx);
}

export function ToastHost({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast["tone"] = "ok", href?: string) => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev, { id, text, tone, href }]);
    window.setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 6000);
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2"
        aria-live="polite"
      >
        {items.map((t) => (
          <div
            key={t.id}
            className="pointer-events-auto rounded border px-3 py-2 text-[12px]"
            style={{
              background: "var(--panel)",
              borderColor:
                t.tone === "danger"
                  ? "var(--danger)"
                  : t.tone === "ok"
                    ? "var(--ok)"
                    : "var(--line-2)",
              boxShadow: "var(--shadow)",
            }}
            data-testid="toast"
          >
            {t.text}
            {t.href ? (
              <>
                {" "}
                <a href={t.href} target="_blank" rel="noreferrer">
                  Open
                </a>
              </>
            ) : null}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
